import { Button, IconIdentifier, Spinner } from '@aus-platform/design-system';
import { isEmpty, isUndefined } from 'lodash';
import { useEffect, useState } from 'react';
import { useAppSelector } from 'src/app/hooks';
import { selectMap3dDataset } from 'src/map-3d/shared/map-3d-slices';
import {
  type ComplianceRequest,
  handleResponseErrorMessage,
  useComplianceAssessmentRequest,
} from 'src/shared/api';
import { EmptyList } from 'src/shared/components';
import { AssessmentTabs } from '../constants';
import { ComplianceRequestItem } from './compliance-assessment-request';

type ComplianceAssessmentHistoryProps = {
  activeTabKey: string;
  setActiveTabKey: (tabKey: string) => void;
};

export const ComplianceAssessmentHistory: React.FC<
  ComplianceAssessmentHistoryProps
> = ({ activeTabKey, setActiveTabKey }) => {
  // useStates.
  const [complianceRequests, setComplianceRequests] = useState<
    ComplianceRequest[]
  >([]);

  // Selectors.
  const { selectedTerrainSite } = useAppSelector(selectMap3dDataset);

  // Apis.
  const {
    data: complianceRequestsResponse,
    isLoading: isLoadingComplianceRequests,
    isError: isErrorComplianceRequests,
    error: complianceRequestsError,
    isSuccess: isSuccessComplianceRequests,
    refetch: refetchComplianceRequestList,
  } = useComplianceAssessmentRequest(selectedTerrainSite?.id || '');

  // useEffects.
  useEffect(() => {
    if (activeTabKey === AssessmentTabs.History) {
      refetchComplianceRequestList();
    }
  }, [activeTabKey]);

  useEffect(() => {
    if (
      isSuccessComplianceRequests &&
      !isUndefined(complianceRequestsResponse.data)
    ) {
      const assessments = complianceRequestsResponse?.data?.assessments ?? [];
      setComplianceRequests(assessments);
    }

    if (isErrorComplianceRequests && !isUndefined(complianceRequestsError)) {
      handleResponseErrorMessage(
        isErrorComplianceRequests,
        complianceRequestsError,
      );
    }
  }, [
    isSuccessComplianceRequests,
    isErrorComplianceRequests,
    complianceRequestsResponse,
    complianceRequestsError,
  ]);

  // Handlers.
  const onClickGenerateRequest = () => setActiveTabKey(AssessmentTabs.Generate);

  if (isLoadingComplianceRequests) {
    return <Spinner />;
  }

  return (
    <div className="compliance-assessment-history">
      {isEmpty(complianceRequests) ? (
        <EmptyList
          headingText="You haven’t generated any compliance assessment requests yet. Generate a request to view it here."
          addButton={
            <Button onClick={onClickGenerateRequest}>Generate Request</Button>
          }
          iconIdentifier={IconIdentifier.FactCheck}
        />
      ) : (
        complianceRequests.map((history: ComplianceRequest) => (
          <ComplianceRequestItem
            key={`${history.createdAt}`}
            complianceRequest={history}
          />
        ))
      )}
    </div>
  );
};
